import { useEffect, useMemo, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { MapPin, Loader2, LocateFixed } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

// Fix default marker icon
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

const TR_CENTER = [39.0, 35.0];

function ClickHandler({ onPick }) {
  useMapEvents({ click(e) { onPick(e.latlng.lat, e.latlng.lng); } });
  return null;
}

function DraggableMarker({ position, onDrag }) {
  const markerRef = useRef(null);
  return (
    <Marker
      position={position}
      draggable
      eventHandlers={{
        dragend() {
          const m = markerRef.current;
          if (m) { const { lat, lng } = m.getLatLng(); onDrag(lat, lng); }
        },
      }}
      ref={markerRef}
    />
  );
}

/**
 * Reusable map picker with click-to-pin + draggable pin + reverse geocoding.
 * Props:
 *  - value: { lat, lng } | null
 *  - onChange({ lat, lng, address, province, district, ... , source })
 *  - height, testid, showLocateBtn
 *  - currentContext: { province, district } — parent'ın mevcut adres bilgisi. Geolocation
 *      sonucu bu context ile ciddi çelişiyorsa (farklı il) koordinat OTOMATİK atanmaz;
 *      kullanıcıya onay sorulur ("Yine de kullan" / "İptal"). Manuel click/drag'de kontrol yok.
 *  - accuracyThresholdM: default 3000m. Bunun üstündeyse kullanıcı uyarılır.
 */
export default function LocationPicker({ value, onChange, height = 260, testid = "loc-picker", showLocateBtn = true, currentContext = null, accuracyThresholdM = 3000 }) {
  const [pos, setPos] = useState(value?.lat && value?.lng ? [value.lat, value.lng] : null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (value?.lat && value?.lng) setPos([value.lat, value.lng]);
  }, [value?.lat, value?.lng]);

  const initialCenter = useMemo(() => pos || TR_CENTER, [pos]);
  const initialZoom = pos ? 14 : 6;

  const reverse = async (lat, lng) => {
    try {
      const r = await fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json&accept-language=tr&zoom=18&addressdetails=1`, {
        headers: { "Accept": "application/json" },
      });
      if (!r.ok) return null;
      return await r.json();
    } catch { return null; }
  };

  const emit = async (lat, lng, source = "manual", accuracy = null) => {
    setPos([lat, lng]);
    const geo = await reverse(lat, lng);
    const a = geo?.address || {};
    const province = a.province || a.state || "";
    const district = a.county || a.district || a.town || a.city_district || "";
    const neighbourhood = a.neighbourhood || a.suburb || a.quarter || a.village || "";
    const road = a.road || "";
    const address = geo?.display_name || "";
    onChange({ lat, lng, province, district, neighbourhood, road, address, source, accuracy });
  };

  // Normalise province names for comparison (accents/case-insensitive Turkish)
  const _norm = (s) => (s || "").toString().toLocaleLowerCase("tr-TR")
    .replaceAll("i̇", "i").replace(/[şŞ]/g, "s").replace(/[çÇ]/g, "c").replace(/[ğĞ]/g, "g")
    .replace(/[üÜ]/g, "u").replace(/[öÖ]/g, "o").replace(/[İI]/g, "i").trim();

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Tarayıcı konum desteklemiyor");
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        const { latitude: lat, longitude: lng, accuracy } = p.coords;
        // Warn on very low accuracy (Wi-Fi/IP-based geolocation on desktop can be 5000m+)
        const accWarn = accuracy && accuracy > accuracyThresholdM
          ? `Konum hassasiyeti düşük (~${Math.round(accuracy)}m). Haritadan pini düzeltebilirsiniz.`
          : null;
        // Contradiction check: if parent already has a province and reverse-geocoded province
        // is a DIFFERENT province, do NOT auto-apply — ask user first. This blocks the
        // classic desktop Wi-Fi/IP-geolocation "İstanbul instead of Manisa" failure mode.
        let auto = true;
        if (currentContext?.province) {
          const geo = await reverse(lat, lng);
          const rvProvince = geo?.address?.province || geo?.address?.state || "";
          if (rvProvince && _norm(rvProvince) !== _norm(currentContext.province)) {
            setBusy(false);
            const useIt = window.confirm(
              `Otomatik konum mevcut adresinizle uyuşmuyor.\n\n` +
              `Kayıtlı il: ${currentContext.province}\nOtomatik konum: ${rvProvince}` +
              (accuracy ? ` (~${Math.round(accuracy)}m hassasiyet)` : "") +
              `\n\nYine de kullanmak ister misiniz?\n(Vazgeç seçerseniz mevcut konumunuz korunur ve haritadan seçebilirsiniz.)`
            );
            if (!useIt) {
              toast.info("Otomatik konum uygulanmadı. Haritadan pini yerleştirin.");
              return;
            }
            await emit(lat, lng, "geolocation", accuracy);
            if (accWarn) toast.warning(accWarn);
            return;
          }
        }
        if (auto) {
          await emit(lat, lng, "geolocation", accuracy);
          if (accWarn) toast.warning(accWarn);
        }
        setBusy(false);
      },
      (err) => {
        setBusy(false);
        const msg = err.code === 1
          ? "Konum izni reddedildi — haritadan seçebilirsiniz"
          : err.code === 3 ? "Konum zaman aşımı — haritadan seçebilirsiniz"
          : "Konumunuz otomatik alınamadı. Haritadan konumunuzu seçebilirsiniz.";
        toast.error(msg);
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 30000 },
    );
  };

  return (
    <div className="space-y-1" data-testid={testid}>
      <div className="rounded-lg overflow-hidden border" style={{ height }}>
        <MapContainer center={initialCenter} zoom={initialZoom} scrollWheelZoom style={{ height: "100%", width: "100%" }}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution='&copy; OpenStreetMap' />
          <ClickHandler onPick={emit} />
          {pos && <DraggableMarker position={pos} onDrag={emit} />}
        </MapContainer>
      </div>
      <div className="flex items-center gap-2 text-xs">
        <MapPin className="w-3 h-3 text-primary" />
        <span className="text-muted-foreground">
          {pos ? `${pos[0].toFixed(5)}, ${pos[1].toFixed(5)}` : "Konum seçmek için haritaya tıklayın veya pini sürükleyin"}
        </span>
        {showLocateBtn && (
          <Button type="button" size="sm" variant="outline" onClick={useMyLocation} disabled={busy} data-testid={`${testid}-locate`} className="ml-auto rounded-full h-7 text-xs">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <LocateFixed className="w-3 h-3 mr-1" />} Konumumu Kullan
          </Button>
        )}
      </div>
    </div>
  );
}
